import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { StagePanel } from './panels/StagePanel';
import { LiveWorkStage } from './LiveWorkStage';
import { DiffBody, MailBody, SlackBody } from './panels/WorkPanels';
import { KostenBody, LisaBody, RenderBody } from './panels/InfoPanels';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import { useDb } from '../../hooks/useDb';
import type { DeskPanel, DeskStage } from './useDeskStage';
const EASE = [0.23, 1, 0.32, 1] as const;
function PanelContent({
  panel,
  stage
}: {
  panel: DeskPanel;
  stage: DeskStage;
}) {
  const pr = useDb(t => t.pullRequests[0]);
  const where = useDb(t => t.renders[0].where);
  const common = {
    pinned: panel.pinned,
    onPin: () => stage.togglePin(panel.uid),
    onDismiss: () => stage.dismiss(panel.uid),
    onOpenBeheer: () => stage.showToast('Beheer komt in een volgende stap.', 'meldt')
  };
  switch (panel.kind) {
    case 'diff':
      return <StagePanel {...common} agent="code" kicker={`PR #${pr.number} · ${pr.repo}`} title={pr.title} level="zelf" where="lokaal" traceId="diff-142" data-id="element-451" data-mp="3isambihg1xxo">
          <DiffBody onOpenGithub={() => stage.showToast('GitHub opent in je browser.', 'meldt')} data-id="element-452" data-mp="3npkm9s2glrzr" />
        </StagePanel>;
    case 'slack':
      return <StagePanel {...common} agent="slack" kicker="vraag aan jou" title="Klant Bakker in #project-x" level="naOk" where="lokaal" traceId="slack-bakker" data-id="element-453" data-mp="el0isguwlqf">
          <SlackBody onPosted={() => stage.approved('slack', 'Antwoord geplaatst in #project-x')} data-id="element-454" data-mp="2zv9iw23jg5vu" />
        </StagePanel>;
    case 'mail':
      return <StagePanel {...common} agent="mail" kicker="concept" title="Antwoord aan klant Bakker" level="naOk" where="cloud" traceId="mail-bakker" data-id="element-455" data-mp="38stmhhovie9m">
          <MailBody onSent={() => stage.approved('mail', 'Mail aan klant Bakker verstuurd')} data-id="element-456" data-mp="3dqlcg65yjqb6" />
        </StagePanel>;
    case 'render':
      return <StagePanel {...common} agent="studio" kicker="render klaar" title="Beeld voor de nieuwe landingspagina" level="zelf" where={where} traceId="render-landing" data-id="element-457" data-mp="t1fed6j64mux">
          <RenderBody onUse={t => stage.showToast(`Toegevoegd aan ${t}`, 'klaar')} data-id="element-458" data-mp="2l4t77waifipv" />
        </StagePanel>;
    case 'lisa':
      return <StagePanel {...common} agent="initiatief" kicker="14:00 · nieuwe klant" title="Voorbereiding Lisa" where="lokaal" traceId="meeting-lisa" data-id="element-459" data-mp="2z39oui9wutms">
          <LisaBody data-id="element-460" data-mp="26euc62e94nem" />
        </StagePanel>;
    case 'kosten':
      return <StagePanel {...common} agent="initiatief" kicker="cloudkosten" title="Kosten vandaag per agent" level="zelf" where="lokaal" traceId="cloud-budget" data-id="element-461" data-mp="21gars6fb3jod">
          <KostenBody data-id="element-462" data-mp="2g6rojcgzxlex" />
        </StagePanel>;
    default:
      return null;
  }
}

/** Center stage: what Anna or the owner is working on right now. Panels arrive from Anna (left) and return to her. */
export function DeskStageArea({
  stage
}: {
  stage: DeskStage;
}) {
  const reduce = useMotionPreference();
  return <section aria-label="Podium" className="no-scrollbar relative min-h-0 overflow-y-auto pb-6" data-id="element-463" data-mp="1inyp1ikqqgdp">
      <AnimatePresence initial={false} mode="popLayout" data-id="element-464" data-mp="3ub1yq4v860td">
        {stage.panels.length === 0 && <motion.div key="live" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-465" data-mp="30blezc9uaeg">
            <LiveWorkStage onOpen={stage.summon} data-id="element-466" data-mp="3pdjsim8ldzp2" />
          </motion.div>}
      </AnimatePresence>
      <div className="flex flex-col gap-5" data-id="element-467" data-mp="gy07ublzlplm">
        <AnimatePresence mode="popLayout" data-id="element-468" data-mp="gw5kkqn8tled">
          {stage.panels.map(p => <motion.div key={p.uid} layout={!reduce} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          x: -220,
          scale: 0.6
        }} animate={{
          opacity: 1,
          x: 0,
          scale: 1
        }} exit={reduce ? {
          opacity: 0,
          transition: {
            duration: 0.2
          }
        } : {
          opacity: 0,
          x: -260,
          scale: 0.3,
          transition: {
            duration: 0.28,
            ease: EASE
          }
        }} transition={reduce ? {
          duration: 0.2
        } : {
          type: 'spring',
          stiffness: 340,
          damping: 30
        }} style={{
          transformOrigin: 'left center'
        }} data-id="element-469" data-mp="lwtaf8zqjw70">
              <PanelContent panel={p} stage={stage} data-id="element-470" data-mp="385ajogedyjrm" />
            </motion.div>)}
        </AnimatePresence>
      </div>
    </section>;
}